import React, { useEffect, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import { showErrorMessage } from 'helpers';
import grapesjs from 'grapesjs';
import grapesjsStyleBgPlugin from 'grapesjs-style-bg';
import grapesjsTuiImageEditorPlugin from 'grapesjs-tui-image-editor';
import { useHistory, Prompt } from 'react-router-dom';
import { difference } from 'lodash';
import Modal from 'react-modal';
import 'grapesjs/dist/css/grapes.min.css';
import 'grapick/dist/grapick.min.css';
import { useSelector, useDispatch } from 'react-redux';
import {
  isAdminSelector,
  getCustomBlocksGroupSelector,
  subscriptionPlanSelector,
} from 'store/selectors';
import {
  deleteCategory,
  updateCustomBlocksGroup,
  createCustomBlocksGroup,
  getCustomBlocksGroup,
} from 'store/actions';
import { FormattedMessage } from 'react-intl';
import {
  ANIMATE_CSS_URL,
  CUSTOM_MODULES_SELECTED_CATEGORIES,
  FONTAWESOME_URL,
  LOAD_STATUS,
  PREVIEW_SIZES,
} from 'appConstants';
import BaseModal, {
  contentStyle,
  overlayStyle,
} from 'components/Modals/BaseModal';
import PersonalNotes from 'components/Modals/PersonalNotes';
import VersionHistory from 'components/Modals/VersionHistory';
import CleanModal from 'components/Modals/CleanModal';
import SvgModal from 'components/Modals/SvgModal';
import ImageModal from 'components/Modals/ImageModal';
import WayMoreFormModal from 'components/Modals/WayMoreFormModal';
import WayMoreProductModal from 'components/Modals/WayMoreProductModal';
import WayMoreCalendarModal from 'components/Modals/WayMoreCalendarModal';
import PageSettingsModal from 'components/Modals/PageSettingsModal';
import BlockModal from 'components/Modals/BlockModal';
import CustomModulCategoryModal from 'components/Modals/CustomModulCategoryModal';
import ConfirmModal from 'components/Modals/ConfirmModal';
import {
  useBlocks,
  useToggleTypes,
  useModal,
  useFont,
  useTemplateImages,
} from 'hooks';
import { httpDownloadSvgIcon } from 'api';
import CodeImportModal from 'components/Modals/CodeImportModal';
import ViewCodeModal from 'components/Modals/ViewCodeModal';
import CustomCodeModal from 'components/Modals/CustomCodeModal';
import CodeEditorModal from 'components/Modals/CodeEditorModal';
import EditorHeader from '../components/EditorHeader';
import PreviewHeader from '../components/PreviewHeader';
import EditorContent from '../components/EditorContent';
import { useEditorContext } from '../EditorContext';
import blocks from './Blocks';
import modules from './Modules';
import waymore from './WayMore';
import panels from './Panels';
import {
  styleManagerConfig,
  panelsConfig,
  commandsConfig,
  canvasCss,
  listeners,
  baseCss,
  protectedCss,
  richTextEditorConfig,
  richTextEditor,
} from './config';
import './Grapes.sass';

const extendBorder = (editor) => {
  editor.StyleManager.addBuiltIn('border', {
    toStyle(values, { property }) {
      const propWidth = property.getProperty('border-width');
      const width = `${propWidth.getValue()}${propWidth.getUnit()}`;
      const style = property.getProperty('border-style').getValue();
      const color = property.getProperty('border-color').getValue();
      return {
        border: `${width} ${style} ${color}`,
        'border-top': '',
        'border-right': '',
        'border-bottom': '',
        'border-left': '',
      };
    },
  });
};

const refreshCustomModulesList = async (
  editor,
  getBlocks,
  categoriesNames,
  addBlock,
  setBlocks,
  setCustomModulesGroupActions,
  deleteModuleCategoryConfirmModal,
  editModuleCategoryModal,
  setCustomModuleGroupsCount,
) => {
  const blocks = await getBlocks();

  const customBlocks = editor.BlockManager.getAll().filter((block) =>
    [
      ...categoriesNames.map((item) => item.name),
      customBlocksCategoryId,
    ].includes(block.get('categorySet')),
  );

  customBlocks.forEach((block) => {
    editor.BlockManager.remove(block.id);
  });

  const classifiedItemsCats = [];

  [
    ...CUSTOM_MODULES_SELECTED_CATEGORIES,
    ...difference(
      categoriesNames.map((item) => item.name.toLowerCase()),
      CUSTOM_MODULES_SELECTED_CATEGORIES,
    ),
  ].forEach((catItem) => {
    const filteredItems = blocks
      .slice()
      .filter(
        (filterItem) => catItem === filterItem.category?.name.toLowerCase(),
      )
      .sort((a, b) => {
        const hasNumA = /\d/.test(a.name);
        const hasNumB = /\d/.test(b.name);

        if (!hasNumA && !hasNumB) {
          return a.name.localeCompare(b.name);
        }

        if (!hasNumA) {
          return -1;
        }

        if (!hasNumB) {
          return 1;
        }

        const numA = parseInt(a.name.match(/\d+/)[0], 10);
        const numB = parseInt(b.name.match(/\d+/)[0], 10);

        return numA - numB;
      });

    if (filteredItems && filteredItems?.length > 0) {
      classifiedItemsCats.push(filteredItems);
    }
  });

  const sortedBlocks = [
    ...classifiedItemsCats,
    ...blocks
      .slice()
      .filter((item) => !item.category)
      .sort((a, b) => b.createdAt - a.createdAt),
  ].flat();

  sortedBlocks.forEach(addBlock);

  await setBlocks(editor);

  const categories = editor.BlockManager.getCategories();

  setCustomModulesGroupActions(
    categories,
    deleteModuleCategoryConfirmModal,
    editModuleCategoryModal,
  );

  const wrapper = editor.getWrapper();

  editor.BlockManager.getCategories().forEach((categoryItem) => {
    categoryItem.set('open', false);

    if (
      wrapper.findType('form').length === 0 &&
      ['Basic'].includes(categoryItem.id)
    ) {
      categoryItem.set('open', true);
    }

    if (
      wrapper.findType('form').length > 0 &&
      ['Forms'].includes(categoryItem.id)
    ) {
      categoryItem.set('open', true);
    }

    if (categoryItem.id === 'My modules') {
      categoryItem.set('attributes', {
        class: 'gjs-block-category--custom',
      });
      categoryItem.set('open', true);
    }
  });

  const CBGroupsCount = new Set();
  sortedBlocks.forEach((item) => {
    if (item?.category?.name !== undefined) {
      CBGroupsCount.add(item.category.name);
    }
  });

  setCustomModuleGroupsCount(CBGroupsCount.size);
};

const customBlocksCategoryId = 'My modules';

const Grapes = ({ onLoad }) => {
  const history = useHistory();

  const dispatch = useDispatch();

  const isAdmin = useSelector(isAdminSelector);

  const categoriesNames = useSelector(getCustomBlocksGroupSelector);
  const subscriptionPlan = useSelector(subscriptionPlanSelector);

  const {
    template: value,
    haveChanges,
    show,
    isUpdateLoading,
    isStatusLoading,
    isConvertLoading,
    onChangeTemplate,
    onChangeInfo,
    onChangeStatus,
    onSaveTemplate,
    onConvertToSystem,
    onShow,
    onChangeAppearanceSettings,
    onAddFont,
    onRestartEditorTour,
  } = useEditorContext();

  const muteChange = useRef(true);
  const editor = useRef(null);

  const {
    isBlocksLoading,
    getBlocks,
    saveBlock,
    deleteBlock,
    setBlocks,
    setCustomModulesGroupActions,
  } = useBlocks();

  const { handleUploadImage } = useTemplateImages(value.id);

  const { generateGoogleFontUrl } = useFont();

  const [isComponentSettingsCollapsed, setIsComponentSettingsCollapsed] =
    useState(false);
  const [isPreview, setIsPreview] = useState(false);
  const [previewSize, setPreviewSize] = useState(PREVIEW_SIZES[0]);
  const [searchBlocksTerm, setSearchBlocksTerm] = useState('');
  const [moduleStatus, setModuleStatus] = useState('create');
  const [moduleTempId, setModuleTempId] = useState(null);
  const [moduleGroupTempId, setModuleGroupTempId] = useState(null);
  const [moduleGroupTempCategory, setModuleGroupTempCategory] = useState({});
  const [moduleDeleteStatus, setModuleDeleteStatus] = useState(
    LOAD_STATUS.IDLE,
  );
  const [moduleGroupDeleteStatus, setModuleGroupDeleteStatus] = useState(
    LOAD_STATUS.IDLE,
  );
  const [moduleGroupEditStatus, setModuleGroupEditStatus] = useState(
    LOAD_STATUS.IDLE,
  );

  const [blocksTab, setBlocksTab] = useState('blocks');
  const [customModuleGroupsCount, setCustomModuleGroupsCount] = useState(0);
  const [formState, setFormState] = useState('normal');
  const [selectedFontsList] = useState(
    value?.fonts
      ? value?.fonts.map(({ family, category, variants, subsets }) => {
          return {
            name: family,
            value: family,
            category,
            url: generateGoogleFontUrl(family, subsets, variants),
          };
        })
      : [],
  );

  useEffect(() => {
    const templateValue = value.template;

    if (value) {
      editor.current = grapesjs.init({
        container: '#canvas',
        storageManager: { type: null },
        cssIcons: FONTAWESOME_URL,
        canvas: {
          styles: [
            ...(templateValue.links
              ? templateValue.links?.map((link) => link.href)
              : []),
            FONTAWESOME_URL,
            ANIMATE_CSS_URL,
          ],
        },
        colorPicker: {
          preferredFormat: 'hex',
          showInput: true,
          showInitial: true,
        },
        avoidInlineStyle: true,
        noticeOnUnload: false,
        allowScripts: 1,
        panels: panelsConfig,
        styleManager: styleManagerConfig,
        commands: commandsConfig,
        richTextEditor: richTextEditorConfig,
        baseCss,
        protectedCss,
        canvasCss,
        showDevices: false,
        height: 'auto', // Oddly defaults to 900px, and is set inline
        width: 'auto',
        blockManager: {
          appendTo: '.editor-content__blocks-list',
        },
        traitManager: {
          appendTo: '#component-settings',
        },
        plugins: [
          extendBorder,
          grapesjsTuiImageEditorPlugin,
          grapesjsStyleBgPlugin,
        ],
        pluginsOpts: {
          [grapesjsTuiImageEditorPlugin]: {
            onApply: async (imageEditor) => {
              const base64Image = imageEditor.toDataURL();
              const applyButton = document.querySelector(
                '.tui-image-editor__apply-btn',
              );
              applyButton.disabled = true;
              applyButton.innerHTML =
                '<span class="icon icon-spinner"></span> Loading';

              try {
                const rndNumber = new Date().valueOf();
                const base64Response = await fetch(base64Image);
                const blob = await base64Response.blob();

                // toDataURL() returns a PNG data URL, so name it .png to match
                // its real MIME type and avoid the API extension/MIME rejection.
                const fileResult = new File(
                  [blob],
                  `edited_image_${rndNumber}.png`,
                  {
                    type: blob.type,
                    lastModified: new Date(),
                  },
                );

                await handleUploadImage([fileResult], [], null, 'image', true);

                editor.current.runCommand('open-assets', { isImage: true });
              } catch (error) {
                showErrorMessage('Something went wrong. Try again!');
              } finally {
                editor.current.Modal.close();
                applyButton.disabled = false;
                applyButton.innerHTML = 'Apply';
              }
            },
            addToAssets: false,
            upload: false,
            width: '100%',
            config: {
              includeUI: {
                initMenu: 'filter',
              },
            },
          },
        },
      });

      // stop tracking changes to prevent undo of the initial setup
      editor.current.UndoManager.stop();

      blocks(
        editor.current,
        subscriptionPlan?.createCustomCode,
        subscriptionPlan?.editCustomCode,
        isAdmin,
      );

      modules(editor.current);
      waymore(editor.current, subscriptionPlan?.waymoreIntegration);
      panels(editor.current);
      listeners(editor.current, selectedFontsList);
      richTextEditor(editor.current);

      editor.current.setComponents(templateValue.components);
      editor.current.setStyle(templateValue.style);

      // resume tracking changes
      editor.current.UndoManager.start();

      editor.current.on('update', () => {
        const wrapper = editor.current.getWrapper();

        if (wrapper.findType('waymore-calendar').length === 0) {
          var scripts =
            editor.current.Canvas.getDocument().getElementsByTagName('script');
          for (var i = 0; i < scripts.length; i++) {
            if (
              scripts[i].textContent.includes(
                'function(i,s,o,g,r,a,m){i["GoogleAnalyticsObject"]',
              )
            ) {
              scripts[i].remove();
            }
          }
        }

        if (
          editor.current.Canvas.getDocument().getElementById(
            'waymore-booking-script',
          )
        ) {
          editor.current.Canvas.getDocument()
            .getElementById('waymore-booking-script')
            .remove();
        }

        const scriptToAdd = document.createElement('script');
        scriptToAdd.type = 'text/javascript';
        scriptToAdd.id = 'waymore-booking-script';
        scriptToAdd.src = 'https://cal.waymore.io/booking.js';

        editor.current.Canvas.getDocument().body.appendChild(scriptToAdd);

        if (!muteChange.current) {
          onChangeTemplate();
        }
        muteChange.current = false;
      });
      editor.current
        .getModel()
        .set(
          'setIsComponentSettingsCollapsed',
          setIsComponentSettingsCollapsed,
        );
      editor.current.getModel().set('onShow', onShow);
      editor.current
        .getModel()
        .set('onChangeAppearanceSettings', onChangeAppearanceSettings);
      editor.current.getModel().set('onAddFont', onAddFont);
      editor.current.getModel().set('setSearchBlocksTerm', setSearchBlocksTerm);
      editor.current.getModel().set('setBlocks', setBlocks);
      editor.current.getModel().set('setBlocksTab', setBlocksTab);
      editor.current.getModel().set('customBlocksGroupList', categoriesNames);
      editor.current
        .getModel()
        .set('setCustomModulesGroupActions', setCustomModulesGroupActions);
      editor.current
        .getModel()
        .set(
          'deleteModuleCategoryConfirmModal',
          deleteModuleCategoryConfirmModal,
        );
      editor.current
        .getModel()
        .set('editModuleCategoryModal', editModuleCategoryModal);
      editor.current.getModel().set('onRestartEditorTour', onRestartEditorTour);
      editor.current.getModel().set('setFormState', setFormState);
      editor.current.getModel().set('formState', formState);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [onChangeTemplate, history, value.templateId]);

  useEffect(() => {
    editor.current.on('load', onLoad);
  }, [onLoad]);

  // callback function to add block to the editor
  // it's defined here because we need closure to onShow, deleteBlock functions
  // and also editor
  const addBlock = (block) => {
    const { id, name, description, html, css, previewUrl, updatedAt } = block;
    let cleanHtml = '';

    // Remove extra container for single column module
    if (
      html?.includes('data-type="column"') &&
      html?.startsWith('<div style="display: flex"')
    ) {
      cleanHtml = html
        .trim()
        .replace('<div style="display: flex">', '')
        .replace(/<\/div>$/, '');
    }

    editor.current.BlockManager.add(id, {
      label: name,
      category: block.category ? block.category.name : customBlocksCategoryId,
      categorySet: block.category
        ? block.category.name
        : customBlocksCategoryId,
      attributes: { class: 'gjs-thumb-label gjs-thumb-label--custom' },
      content: `${cleanHtml ? cleanHtml : html}<style>${css}</style>`,
      links: block?.links,
      type: 'custom-module',
      render: ({ el }) => {
        const buttonsWrapper = document.createElement('div');
        buttonsWrapper.className = 'gjs-thumb-label__buttons-wrapper';

        const editButton = document.createElement('button');
        editButton.title = 'Edit';
        editButton.className = `gjs-thumb-label__btn ${
          isAdmin && block?.category?.group === 'system'
            ? 'secondary'
            : 'default'
        }`;
        editButton.innerHTML = '<span class="icon icon-edit"></span>';
        editButton.addEventListener('click', function () {
          onShow(useToggleTypes.showBlockModal, block);
          setModuleStatus('Edit');
        });

        const deleteButton = document.createElement('button');
        deleteButton.title = 'Delete';
        deleteButton.className = `gjs-thumb-label__btn ${
          isAdmin && block?.category?.group === 'system'
            ? 'secondary'
            : 'default'
        }`;
        deleteButton.innerHTML = '<span class="icon icon-delete"></span>';
        deleteButton.addEventListener('click', async function () {
          openConfirmDeleteModal();
          setModuleTempId(id);
        });

        if (!isAdmin && !block.category) {
          buttonsWrapper.appendChild(editButton);
          buttonsWrapper.appendChild(deleteButton);
        } else if (isAdmin) {
          buttonsWrapper.appendChild(editButton);
          buttonsWrapper.appendChild(deleteButton);
        }

        el.innerHTML = `<img src="${previewUrl}?${updatedAt}" alt="${name}"/>`;
        el.appendChild(buttonsWrapper);

        // Hover over every custom module
        const customModulePreview = document.querySelector(
          '.custom-module-preview',
        );
        const customModulePreviewHover = () => {
          customModulePreview.querySelector('h1').innerText = name;
          customModulePreview.querySelector('p').innerText = description;
          customModulePreview
            .querySelector('img')
            .setAttribute('src', previewUrl);
          customModulePreview.querySelector('img').setAttribute('alt', name);
          customModulePreview.classList.add('custom-module-preview__show');
        };
        const customModulePreviewHoverOut = () => {
          customModulePreview.classList.remove('custom-module-preview__show');
          customModulePreview
            .querySelector('img')
            .setAttribute('src', '/images/image-loading.svg');
        };

        el.addEventListener('mouseenter', customModulePreviewHover);
        el.addEventListener('mouseleave', customModulePreviewHoverOut);
        el.addEventListener('drag', customModulePreviewHoverOut);
      },
    });
  };

  useEffect(() => {
    editor.current.on('load', async () => {
      refreshCustomModulesList(
        editor.current,
        getBlocks,
        categoriesNames,
        addBlock,
        setBlocks,
        setCustomModulesGroupActions,
        deleteModuleCategoryConfirmModal,
        editModuleCategoryModal,
        setCustomModuleGroupsCount,
      );
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [deleteBlock, getBlocks, onShow]);

  useEffect(() => {
    if (haveChanges) {
      window.onbeforeunload = () =>
        'You have unsaved work. If you continue it will be lost. Are you sure you want to leave?';
    } else {
      window.onbeforeunload = null;
    }
    return () => {
      window.onbeforeunload = null;
    };
  }, [haveChanges]);

  const deleteModuleCategoryConfirmModal = (id) => {
    setModuleGroupTempId(id);
    openConfirmDeleteModuleCatModal();
  };

  const editModuleCategoryModal = (groupName) => {
    const [{ id, name }] = categoriesNames.filter(
      (item) => item.name === groupName,
    );
    setModuleGroupTempCategory({
      id,
      name,
    });
    onShow(useToggleTypes.showCustomModulCategoryModal);
  };

  const handleSaveCustomModuleCategory = async (values) => {
    try {
      setModuleGroupEditStatus(LOAD_STATUS.LOADING);

      if (moduleGroupTempCategory) {
        await dispatch(
          updateCustomBlocksGroup({
            id: moduleGroupTempCategory.id,
            name: values.name.trim(),
          }),
        ).unwrap();
      } else {
        await dispatch(
          createCustomBlocksGroup({ name: values.name.trim() }),
        ).unwrap();
      }

      handleCloseCustomModulCategoryModal();
      setModuleGroupEditStatus(LOAD_STATUS.RESOLVED);
    } catch {
      setModuleGroupEditStatus(LOAD_STATUS.REJECTED);
    } finally {
      const customBlocksGroupList = await dispatch(
        getCustomBlocksGroup(),
      ).unwrap();

      setModuleGroupTempCategory(null);
      editor.current
        .getModel()
        .set('customBlocksGroupList', customBlocksGroupList);

      await refreshCustomModulesList(
        editor.current,
        getBlocks,
        categoriesNames,
        addBlock,
        setBlocks,
        setCustomModulesGroupActions,
        deleteModuleCategoryConfirmModal,
        editModuleCategoryModal,
        setCustomModuleGroupsCount,
      );
    }
  };

  const handleDeleteModuleCategory = async () => {
    try {
      setModuleGroupDeleteStatus(LOAD_STATUS.LOADING);
      const [{ id }] = categoriesNames.filter(
        (item) => item.name === moduleGroupTempId,
      );

      await dispatch(deleteCategory(id)).unwrap();
      setModuleGroupDeleteStatus(LOAD_STATUS.RESOLVED);
    } catch {
      setModuleGroupDeleteStatus(LOAD_STATUS.REJECTED);
    } finally {
      closeConfirmDeleteModuleCatModal();
      setModuleGroupTempId(null);
      refreshCustomModulesList(
        editor.current,
        getBlocks,
        categoriesNames,
        addBlock,
        setBlocks,
        setCustomModulesGroupActions,
        deleteModuleCategoryConfirmModal,
        editModuleCategoryModal,
        setCustomModuleGroupsCount,
      );
    }
  };

  const handleDeleteModule = async () => {
    try {
      setModuleDeleteStatus(LOAD_STATUS.LOADING);
      await deleteBlock(moduleTempId);

      editor.current.BlockManager.remove(moduleTempId);
      setModuleDeleteStatus(LOAD_STATUS.RESOLVED);
    } catch {
      setModuleDeleteStatus(LOAD_STATUS.REJECTED);
    } finally {
      setModuleTempId(null);
      closeConfirmDeleteModal();
      refreshCustomModulesList(
        editor.current,
        getBlocks,
        categoriesNames,
        addBlock,
        setBlocks,
        setCustomModulesGroupActions,
        deleteModuleCategoryConfirmModal,
        editModuleCategoryModal,
        setCustomModuleGroupsCount,
      );
    }
  };

  const handlePreviewSizeChange = (size = 'pc') => {
    editor.current.Panels.getButton(
      'responsive-actions',
      `preview-${size === 'pc' ? 'desktop' : size}`,
    ).set('active', true);

    const canvasFrames = document.getElementsByClassName(
      'gjs-cv-canvas__frames',
    )[0];

    canvasFrames.classList.remove(...PREVIEW_SIZES);
    canvasFrames.classList.add(currentDevicePreview());

    setPreviewSize(size);
  };

  const handleSetSvgIcon = async (svgDownloadUrl) => {
    const svgIcon = await httpDownloadSvgIcon(svgDownloadUrl);

    editor.current.runCommand('set-svg-icon', { svgIcon });

    onShow(useToggleTypes.showSvgModal);
  };

  const handleSetImageUrl = ({ url, isIcon }) => {
    if (url) {
      editor.current.runCommand('set-image-url', { url });
    }

    if (isIcon) {
      onShow(useToggleTypes.showIconModal);
      return;
    }

    onShow(useToggleTypes.showImageModal);
  };

  const handleSetWayMoreFormSrc = (src) => {
    editor.current.runCommand('set-waymore-form-src', { src });

    onShow(useToggleTypes.showWayMoreFormModal);
  };

  const handleCloseWayMoreFormModal = () => {
    const selected = editor.current.getSelected();

    const src = selected.get('src');

    if (!src) {
      selected.remove();
    }

    onShow(useToggleTypes.showWayMoreFormModal);
  };

  const handleCloseWayMoreProductModal = () => {
    const selected = editor.current.getSelected();

    if (!selected.view.el.innerHTML) {
      selected.remove();
    }
    onShow(useToggleTypes.showWayMoreProductModal);
  };

  const handleSaveWayMoreProduct = (
    layout,
    showCategory,
    showDescription,
    product,
  ) => {
    const selected = editor.current.getSelected();

    editor.current.runCommand('set-waymore-product', {
      layout,
      showCategory,
      showDescription,
      product,
      oldProduct: selected,
    });

    selected.remove();

    onShow(useToggleTypes.showWayMoreProductModal);
  };

  const handleCloseWayMoreCalendarModal = () => {
    onShow(useToggleTypes.showWayMoreCalendarModal);
  };

  const handleSaveWayMoreCalendar = (calendarId, serviceId, noHeader) => {
    editor.current.runCommand('set-waymore-calendar', {
      calendarId,
      serviceId,
      noHeader,
    });

    onShow(useToggleTypes.showWayMoreCalendarModal);
  };

  const handleSaveAppearanceSettings = (payload) => {
    editor.current.runCommand('set-appearance-settings', payload);
  };

  const handleClosePageSettings = () => {
    onShow(useToggleTypes.showPageSettings);
  };

  const handleClosePersonalNotes = () => {
    onShow(useToggleTypes.showPersonalNotesModal);
  };

  const handleCloseVersionHistory = () => {
    onShow(useToggleTypes.showVersionHistory);
  };

  const handleCloseCustomModulCategoryModal = () => {
    onShow(useToggleTypes.showCustomModulCategoryModal);
  };

  const handleSaveTemplate = () => {
    onSaveTemplate(editor.current);
  };

  const handleChangeSearchBlocksTerm = async ({ target: { value } }) => {
    setSearchBlocksTerm(value);
    await setBlocks(editor.current, value);
  };

  const handleCloseBlockModal = () => {
    onShow(useToggleTypes.showBlockModal);
  };

  const handleCloseCodeImportModal = () => {
    onShow(useToggleTypes.showCodeImportModal);
  };

  const handleCloseViewCodeModal = () => {
    onShow(useToggleTypes.showViewCodeModal);
  };

  const handleCloseCustomCodeModal = () => {
    onShow(useToggleTypes.showCustomCodeModal);
  };

  const handleCloseAddClickEvent = () => {
    onShow(useToggleTypes.showAddClickEventModal);
  };

  const handleSaveBlock = async (payload) => {
    try {
      await saveBlock(payload, isAdmin);

      handleCloseBlockModal();
      // eslint-disable-next-line no-empty
    } catch {
    } finally {
      await refreshCustomModulesList(
        editor.current,
        getBlocks,
        categoriesNames,
        addBlock,
        setBlocks,
        setCustomModulesGroupActions,
        deleteModuleCategoryConfirmModal,
        editModuleCategoryModal,
        setCustomModuleGroupsCount,
      );
    }
  };

  const togglePreview = () => {
    if (!isPreview) {
      // enable preview
      setIsPreview(true);

      // run original command
      editor.current.runCommand('preview');

      // add preview class for canvas
      const canvas = document.getElementsByClassName('gjs-cv-canvas')[0];
      canvas.classList.add('preview');

      // remove default close button
      const defaultCloseBtn =
        document.getElementsByClassName('fa-eye-slash')[0];
      if (defaultCloseBtn) {
        defaultCloseBtn.remove();
      }

      // hide iframe scrollbar
      const iframe = document.getElementsByClassName('gjs-frame')[0];
      const iframeDocument =
        iframe.contentDocument || iframe.contentWindow.document;
      if (iframeDocument) {
        const css = `
          *::-webkit-scrollbar {
            width: 0px;
          }
          [data-gjs-type="column"]:empty:before {
            content: none;
          }
        `;
        const style = iframeDocument.createElement('style');
        style.type = 'text/css';
        style.id = 'preview-style';
        style.setAttribute('id', 'preview-style');
        if (style.styleSheet) {
          style.styleSheet.cssText = css;
        } else {
          style.appendChild(iframeDocument.createTextNode(css));
        }
        iframeDocument.getElementsByTagName('body')[0].appendChild(style);
      }

      // add size preview class for frame container
      const canvasFrames = document.getElementsByClassName(
        'gjs-cv-canvas__frames',
      )[0];
      canvasFrames.classList.add(currentDevicePreview());
      return;
    }

    // disable preview
    setIsPreview(false);

    // restore iframe scrollbar
    const iframe = document.getElementsByClassName('gjs-frame')[0];
    const iframeDocument =
      iframe.contentDocument || iframe.contentWindow.document;
    if (iframeDocument) {
      const previewStyle = iframeDocument.getElementById('preview-style');
      iframeDocument.getElementsByTagName('body')[0].removeChild(previewStyle);
    }

    // remove preview class from canvas
    const canvas = document.getElementsByClassName('gjs-cv-canvas')[0];
    canvas.classList.remove('preview');

    // remove size preview class from frame container
    const canvasFrames = document.getElementsByClassName(
      'gjs-cv-canvas__frames',
    )[0];
    canvasFrames.classList.remove(previewSize);

    // stop original command
    editor.current.stopCommand('preview');

    // refresh editor
    editor.current.refresh();
  };

  const currentDevicePreview = () => {
    const currectDevice = ['desktop', 'tablet', 'mobile']
      .filter((device) =>
        editor.current.Panels.getButton(
          'responsive-actions',
          `preview-${device}`,
        ).get('active'),
      )
      .join('');

    return currectDevice === 'desktop' ? 'pc' : currectDevice;
  };

  const {
    isOpen: isConfirmDeleteModalOpen,
    open: openConfirmDeleteModal,
    close: closeConfirmDeleteModal,
  } = useModal();

  const {
    isOpen: isConfirmDeleteModuleCatModalOpen,
    open: openConfirmDeleteModuleCatModal,
    close: closeConfirmDeleteModuleCatModal,
  } = useModal();

  return (
    <div id="grapes" className="grapes">
      <EditorHeader
        template={value}
        isConvertLoading={isConvertLoading}
        isUpdateLoading={isUpdateLoading}
        isStatusLoading={isStatusLoading}
        haveChanges={haveChanges}
        show={show}
        onShow={onShow}
        onConvertToSystem={onConvertToSystem}
        onChangeStatus={() => onChangeStatus(editor.current)}
        onSaveTemplate={handleSaveTemplate}
        onChangeInfo={onChangeInfo}
        onTogglePreview={togglePreview}
      />
      {isPreview && (
        <PreviewHeader
          onPreviewSizeChange={handlePreviewSizeChange}
          onTogglePreview={togglePreview}
          currentDevicePreview={currentDevicePreview()}
        />
      )}
      <EditorContent
        editor={editor.current}
        searchBlocksTerm={searchBlocksTerm}
        onChangeSearchBlocksTerm={handleChangeSearchBlocksTerm}
        onShow={editor?.current?.getModel().get('onShow')}
        blocksTab={blocksTab}
        customModuleGroupsCount={customModuleGroupsCount}
        setModuleGroupTempCategory={setModuleGroupTempCategory}
        setIsComponentSettingsCollapsed={setIsComponentSettingsCollapsed}
        isComponentSettingsCollapsed={isComponentSettingsCollapsed}
        formState={formState}
      />
      <BaseModal
        isOpen={show.clean}
        onClose={() => {
          onShow(useToggleTypes.showClean);
        }}
        style={{ width: '600px', maxWidth: '100%' }}
        headerTitle="clean-modal.title"
        iconTitleClass="icon icon-delete">
        <CleanModal
          isOpen={show.clean}
          onClose={() => {
            onShow(useToggleTypes.showClean);
          }}
          handleClean={() => {
            editor.current.runCommand('core:canvas-clear');
            editor.current.select(editor.current.getWrapper());

            // Removing extra google fonts links
            Array.from(
              editor.current.Canvas.getDocument().querySelectorAll('link'),
            )
              .filter((item) =>
                item
                  .getAttribute('href')
                  .startsWith('https://fonts.googleapis.com/css2?family'),
              )
              .forEach((item) => item.remove());
          }}
        />
      </BaseModal>
      <BaseModal
        isOpen={show.svgModal}
        onClose={() => onShow(useToggleTypes.showSvgModal)}
        headerTitle="svg-modal.title">
        <SvgModal onSetSvgIcon={handleSetSvgIcon} />
      </BaseModal>
      <BaseModal
        isOpen={show.showImageModal}
        onClose={() => onShow(useToggleTypes.showImageModal)}
        iconTitleClass="icon icon-image"
        headerTitle="image-modal.title">
        <ImageModal
          id={value.id}
          editor={editor.current}
          onSetImageUrl={handleSetImageUrl}
        />
      </BaseModal>
      <BaseModal
        isOpen={show.showIconModal}
        onClose={() => onShow(useToggleTypes.showIconModal)}
        headerTitle="icon-modal.title">
        <ImageModal
          withIconsTab
          id={value.id}
          editor={editor.current}
          onSetImageUrl={handleSetImageUrl}
        />
      </BaseModal>
      <BaseModal
        isOpen={show.wayMoreFormModal}
        onClose={handleCloseWayMoreFormModal}
        headerTitle="waymore-form-modal.title"
        style={{ maxWidth: 600, overflow: 'visible' }}
        contentClass="waymore-form-modal">
        <WayMoreFormModal onSetWayMoreFormSrc={handleSetWayMoreFormSrc} />
      </BaseModal>
      <Modal
        isOpen={show.wayMoreProductModal}
        onRequestClose={handleCloseWayMoreProductModal}
        style={{
          overlay: overlayStyle,
          content: {
            ...contentStyle,
            maxWidth: 600,
            overflow: 'visible',
          },
        }}>
        <WayMoreProductModal
          editor={editor?.current}
          onSaveWayMoreProduct={handleSaveWayMoreProduct}
          onClose={handleCloseWayMoreProductModal}
        />
      </Modal>
      <Modal
        isOpen={show.wayMoreCalendarModal}
        onRequestClose={handleCloseWayMoreCalendarModal}
        style={{
          overlay: overlayStyle,
          content: {
            ...contentStyle,
            maxWidth: 600,
            overflow: 'visible',
          },
        }}>
        <WayMoreCalendarModal
          onSaveWayMoreCalendar={handleSaveWayMoreCalendar}
          onClose={handleCloseWayMoreCalendarModal}
        />
      </Modal>
      <BaseModal
        isOpen={show.pageSettings}
        onClose={handleClosePageSettings}
        iconTitleClass="icon icon-settings"
        headerTitle="page-settings-modal.title"
        contentClass="page-settings-modal">
        <PageSettingsModal
          editor={editor.current}
          onSaveAppearanceSettings={handleSaveAppearanceSettings}
          onClose={handleClosePageSettings}
          subscriptionPlan={subscriptionPlan}
        />
      </BaseModal>
      <BaseModal
        isOpen={show.blockModal}
        onClose={handleCloseBlockModal}
        maxWidth="80%"
        headerTitle={
          moduleStatus === 'create'
            ? 'block-modal.title.create'
            : 'block-modal.title.edit'
        }
        contentClass="block-modal">
        <BlockModal
          block={show.blockModalPayload}
          isBlocksLoading={isBlocksLoading}
          onSaveBlock={handleSaveBlock}
          onClose={handleCloseBlockModal}
          templateId={value.id}
          editor={editor?.current}
          selectedFontsList={selectedFontsList}
        />
      </BaseModal>
      <BaseModal
        isOpen={show.codeImportModal}
        onClose={handleCloseCodeImportModal}
        headerTitle="code-import-modal.modal-header"
        contentClass="code-import-content-modal"
        maxWidth="80%">
        <CodeImportModal
          editor={editor?.current}
          onClose={handleCloseCodeImportModal}
        />
      </BaseModal>
      <BaseModal
        isOpen={show.viewCodeModal}
        onClose={handleCloseViewCodeModal}
        headerTitle="view-code-modal.modal-header"
        contentClass="view-code-content-modal"
        maxWidth="80%">
        <ViewCodeModal editor={editor?.current} />
      </BaseModal>
      <BaseModal
        isOpen={show.customCodeModal}
        onClose={handleCloseCustomCodeModal}
        headerTitle="custom-code-modal.modal-header"
        contentClass="custom-code-content-modal"
        maxWidth="80%">
        <CustomCodeModal
          editor={editor?.current}
          onClose={handleCloseCustomCodeModal}
        />
      </BaseModal>
      <Prompt
        when={haveChanges}
        message="You have unsaved work. If you continue it will be lost. Are you sure you want to leave?"
      />
      <BaseModal
        isOpen={isConfirmDeleteModalOpen}
        headerTitle="block-modal.confirm-modal-header"
        maxWidth="600px"
        onClose={closeConfirmDeleteModal}>
        <ConfirmModal
          loading={moduleDeleteStatus === LOAD_STATUS.LOADING}
          buttonsColor="secondary"
          onClose={closeConfirmDeleteModal}
          onConfirm={handleDeleteModule}>
          <FormattedMessage id="block-modal.confirm-modal-description" />
        </ConfirmModal>
      </BaseModal>
      <BaseModal
        iconTitleClass="icon icon-add-comment"
        isOpen={show.showPersonalNotesModal}
        onClose={handleClosePersonalNotes}
        maxWidth="600px"
        headerTitle="personal-notes.modal-title">
        <PersonalNotes onClose={handleClosePersonalNotes} />
      </BaseModal>
      <BaseModal
        contentClass="version-history-content"
        contentStyle={{
          padding: 0,
        }}
        headerStyle={{
          margin: 0,
          position: 'sticky',
          top: 0,
          background: '#fff',
        }}
        style={{
          maxWidth: '400px',
          borderRadius: 0,
          boxShadow: 'none',
          margin: 0,
          padding: '0 !important',
          border: 'none',
        }}
        iconTitleClass="icon icon-history"
        isOpen={show.showVersionHistory}
        onClose={handleCloseVersionHistory}
        headerTitle="version-history.modal-title">
        <VersionHistory
          editor={editor.current}
          onSaveTemplate={onSaveTemplate}
          onClose={handleCloseVersionHistory}
        />
      </BaseModal>
      <BaseModal
        isOpen={show.showCustomModulCategoryModal}
        onClose={handleCloseCustomModulCategoryModal}
        maxWidth="600px"
        headerTitle="custom-modules-category.modal-title">
        <CustomModulCategoryModal
          onClose={handleCloseCustomModulCategoryModal}
          categoriesNames={categoriesNames}
          category={moduleGroupTempCategory}
          onSaveCustomModuleCategory={handleSaveCustomModuleCategory}
          isLoading={moduleGroupEditStatus === LOAD_STATUS.LOADING}
        />
      </BaseModal>
      <BaseModal
        isOpen={isConfirmDeleteModuleCatModalOpen}
        headerTitle="custom-modules.confirm-modal-header"
        maxWidth="600px"
        onClose={closeConfirmDeleteModuleCatModal}>
        <ConfirmModal
          loading={moduleGroupDeleteStatus === LOAD_STATUS.LOADING}
          buttonsColor="secondary"
          onClose={closeConfirmDeleteModuleCatModal}
          onConfirm={handleDeleteModuleCategory}>
          <FormattedMessage id="custom-modules.confirm-modal-description" />
        </ConfirmModal>
      </BaseModal>
      <BaseModal
        isOpen={show.addClickEventModal}
        headerTitle="custom-modules.add-click-modal-header"
        maxWidth="600px"
        onClose={handleCloseAddClickEvent}>
        <CodeEditorModal
          editor={editor?.current}
          onClose={handleCloseAddClickEvent}
        />
      </BaseModal>
    </div>
  );
};
Grapes.propTypes = {
  onLoad: PropTypes.func.isRequired,
};

export default Grapes;
